type EnvVarKey =
  | 'VITE_API_HOST'
  | 'VITE_API_CLIENT_ID'
  | 'VITE_API_CSRFTOKEN'
  | 'VITE_API_DEVELOPMENT'
  | 'VITE_DELAY_IN_SECONDS_BEFORE_VERIFICATION'
  | 'VITE_VERSION'
  | 'VITE_GOOGLE_API_KEY';

type RuntimeEnv = Partial<Record<EnvVarKey, string>>;

const runtimeEnv = (() => {
  if (typeof window === 'undefined') {
    return {} as RuntimeEnv;
  }

  return (window.__ENV__ || {}) as RuntimeEnv;
})();

const buildEnv = import.meta.env as Record<string, string | undefined>;

const readEnv = (key: EnvVarKey): string => {
  const runtimeValue = runtimeEnv[key];
  if (runtimeValue !== undefined && runtimeValue !== '') {
    return runtimeValue;
  }

  const buildValue = buildEnv[key];
  return buildValue ?? '';
};

export const env = {
  apiHost: readEnv('VITE_API_HOST'),
  apiClientId: readEnv('VITE_API_CLIENT_ID'),
  apiCsrfToken: readEnv('VITE_API_CSRFTOKEN'),
  apiDevelopment: readEnv('VITE_API_DEVELOPMENT'),
  delayBeforeVerification: readEnv('VITE_DELAY_IN_SECONDS_BEFORE_VERIFICATION'),
  version: readEnv('VITE_VERSION'),
  googleApiKey: readEnv('VITE_GOOGLE_API_KEY'),
};

const STAGE_API_HOST = 'https://adminpanel.stage.drivitive.fr';
const PROD_API_HOST = 'https://adminpanel.drivitive.fr';

const isBrowser = typeof window !== 'undefined';

const resolveConfiguredHost = (): string | undefined => {
  const host = env.apiHost?.trim();
  if (host) {
    return host;
  }

  if (!isBrowser || !window.__ENV__) {
    return undefined;
  }

  const runtimeHost = window.__ENV__?.VITE_API_HOST?.trim();
  return runtimeHost || undefined;
};

const isStageFrontend = (): boolean => {
  if (!isBrowser) {
    return true;
  }

  const hostname = window.location.hostname;
  return hostname.includes('stage') || hostname.includes('dev');
};

const isLocalFrontend = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname.includes('localhost');
};

export const getApiBaseUrl = (): string => {
  // Use proxy for development from localhost
  if (isLocalFrontend()) {
    return '';
  }

  const configuredHost = resolveConfiguredHost();
  if (configuredHost) {
    return configuredHost;
  }

  if (isDevelopmentEnv() || isStageFrontend()) {
    return STAGE_API_HOST;
  }

  return PROD_API_HOST;
};

export const isDevelopmentEnv = (): boolean => {
  const flag = env.apiDevelopment?.toLowerCase?.() || '';
  return flag === 'true' || flag === '1';
};

export const buildApiUrl = (path: string): string => {
  if (path.startsWith('http')) {
    return path;
  }

  const base = getApiBaseUrl();
  return `${base}${path}`;
};
